import { useMemo, useState } from "react";
import { CheckCircle2, Plus } from "lucide-react";
import {
  Body,
  Caption,
  Card,
  Subtitle,
  VButton,
  VDialog,
  VDialogContent,
  VDialogTrigger,
  cn,
} from "@/components/ui";
import { useCurrentUser } from "@/domains/auth";
import { useAvailableAssets } from "@/domains/assets";
import {
  AssetTypeIcon,
  AssetStatusBadge,
  assetTypeLabel,
  stripAssetTypePrefix,
} from "@/domains/assets";
import type { Asset, AssetType } from "@/domains/assets";
import { useAddAssetToCase } from "@/domains/cases/api/mutations";
import type { CaseFile } from "@/domains/cases/api/types";

type AssetPickerDialogProps = {
  caseFile: CaseFile;
};

const TYPE_GROUPS: ReadonlyArray<{ type: AssetType; label: string }> = [
  { type: "camera", label: "Mobiele camera's" },
  { type: "mobileTree", label: "Mobile trees" },
  { type: "camcar", label: "Camcars" },
  { type: "batteryPack", label: "Batterij packs" },
  { type: "trailer", label: "Trailers" },
];

export function AssetPickerDialog({ caseFile }: AssetPickerDialogProps): React.JSX.Element {
  const currentUser = useCurrentUser();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const { data: available, isPending } = useAvailableAssets(
    caseFile.startDate,
    caseFile.endDate,
  );
  const addAsset = useAddAssetToCase();

  const grouped = useMemo(() => {
    const list = (available ?? []).filter((a) => !caseFile.assetIds.includes(a.id));
    const byType = new Map<AssetType, Array<Asset>>();
    for (const asset of list) {
      const arr = byType.get(asset.type) ?? [];
      arr.push(asset);
      byType.set(asset.type, arr);
    }
    return byType;
  }, [available, caseFile.assetIds]);

  const toggle = (id: string): void => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleConfirm = async (): Promise<void> => {
    const actor = {
      id: currentUser.id,
      name: `${currentUser.firstName} ${currentUser.lastName}`,
    };
    for (const assetId of selected) {
      await addAsset.mutateAsync({ caseId: caseFile.id, assetId, actor });
    }
    setSelected(new Set());
    setOpen(false);
  };

  return (
    <VDialog open={open} onOpenChange={setOpen}>
      <VDialogTrigger asChild>
        <VButton variant="primary">
          <Plus className="size-4" aria-hidden />
          Asset toevoegen
        </VButton>
      </VDialogTrigger>
      <VDialogContent
        size="xl"
        title="Stel beschikbare assets voor"
        description={`Periode: ${caseFile.startDate} → ${caseFile.endDate}. Enkel assets die in deze periode vrij staan worden getoond.`}
      >
        {isPending ? (
          <Subtitle>Beschikbaarheid berekenen…</Subtitle>
        ) : grouped.size === 0 ? (
          <Subtitle>Geen extra beschikbare assets voor deze periode.</Subtitle>
        ) : (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto">
            {TYPE_GROUPS.map(({ type, label }) => {
              const items = grouped.get(type) ?? [];
              if (items.length === 0) return null;
              return (
                <section key={type} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AssetTypeIcon type={type} className="size-4 text-muted-foreground" />
                    <Body weight="semibold">{label}</Body>
                    <Caption tone="muted">({items.length} beschikbaar)</Caption>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {items.map((asset) => {
                      const isSelected = selected.has(asset.id);
                      return (
                        <Card
                          key={asset.id}
                          padding="md"
                          interactive
                          onClick={() => toggle(asset.id)}
                          className={cn(
                            "flex items-center justify-between gap-3",
                            isSelected && "border-primary-600 bg-primary-50",
                          )}
                        >
                          <div className="min-w-0">
                            <Body weight="semibold">
                              {asset.number} — {assetTypeLabel(asset.type)}
                            </Body>
                            <Caption as="p" tone="muted" className="truncate">
                              {stripAssetTypePrefix(asset.name, asset.type)}
                            </Caption>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <AssetStatusBadge status={asset.status} />
                            {isSelected ? (
                              <CheckCircle2
                                className="size-4 text-primary-600"
                                aria-hidden
                              />
                            ) : null}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
          <Caption tone="muted">
            {selected.size === 0
              ? "Selecteer een of meerdere assets"
              : `${selected.size} asset(s) geselecteerd`}
          </Caption>
          <div className="flex gap-2">
            <VButton variant="outline" onClick={() => setOpen(false)}>
              Annuleren
            </VButton>
            <VButton
              onClick={handleConfirm}
              disabled={selected.size === 0 || addAsset.isPending}
            >
              Koppel aan dossier
            </VButton>
          </div>
        </div>
      </VDialogContent>
    </VDialog>
  );
}
